<template>
  <div class="fixed inset-0 overflow-y-auto z-50">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-500 opacity-75" @click="close"></div>
      </div>

      <!-- Modal content -->
      <div class="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
        <!-- Header -->
        <div class="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 px-6 py-4 text-white">
          <div class="flex justify-between items-center">
            <h2 class="text-xl font-bold">جزئیات گزارش: {{ report.name }}</h2>
            <button @click="close" class="text-blue-100 hover:text-white">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="mt-2 flex flex-wrap gap-4 text-sm">
            <div class="flex items-center">
              <svg class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              تاریخ گزارش: {{ formatDate(report.date) }}
            </div>
            <div class="flex items-center">
              <svg class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              تاریخ ایجاد: {{ formatDateTime(report.created_at) }}
            </div>
            <div class="flex items-center">
              <svg class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              تعداد آیتم‌ها: {{ report.items_count }}
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="px-6 py-4">
          <!-- Search and filters -->
          <div class="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div class="relative w-full md:w-64">
              <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="جستجوی کد رهگیری یا سفارش"
                  class="px-2 py-1.5 bg-gray-100 text-xs rounded-lg border w-full text-right"
              >
            </div>
          </div>

          <!-- Items table -->
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  وضعیت
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  کد رهگیری پستی
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                   سفارش
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  تاریخ ایجاد
                </th>
              </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="item in filteredItems" :key="item.id">
                <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[item.status === 'f' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800', 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full']">
                      {{ item.status === 'f' ? 'سفارش' : 'نا معلوم' }}
                    </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ item.post_tracking_code }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <template v-if="item.shop_order">
                    <router-link
                        target="_blank"
                        :to="`/panel/adminOrderDetail/${item.shop_order.id}`"
                        class="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      #{{ item.shop_order.id }} {{item.shop_order.shipment_address.first_name}} {{item.shop_order.shipment_address.last_name}}
                    </router-link>
                  </template>
                  <template v-else>
                    {{item.explanation}}
                  </template>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDateTime(item.created_at) }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty state -->
          <div v-if="filteredItems.length === 0 && loading === false" class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">آیتمی یافت نشد</h3>
            <p class="mt-1 text-sm text-gray-500">هیچ آیتمی با فیلترهای انتخاب شده مطابقت ندارد.</p>
          </div>

          <div v-else-if="filteredItems.length === 0 && loading === true" class="text-center py-8">
            <h3 class="mt-2 text-sm font-medium text-gray-900">در حال بارگذاری ...</h3>
          </div>

          <!-- Pagination -->
          <div class="mt-4 flex items-center justify-between">
            <div class="flex-1">
              <p class="text-sm text-gray-700">
                نمایش
                <span class="font-medium">{{ filteredItems.length }}</span>
                از
                <span class="font-medium">{{ report.items_count }}</span>
                آیتم
              </p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 px-6 py-3 flex justify-end">
          <button
              @click="close"
              type="button"
              class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            بستن
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExtractedPostReportDetails',
  props: {
    report: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      items: [],
      loading: false,
      searchQuery: '',
      typeFilter: 'n'
    };
  },
  computed: {
    filteredItems() {
      let filtered = this.items;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(item =>
            item.post_tracking_code.toLowerCase().includes(query) ||
            (item.shop_order && item.shop_order.id.toString().includes(query)) ||
            (item.shop_order && item.shop_order.shipment_address && item.shop_order.shipment_address.first_name.includes(query)) ||
            (item.shop_order && item.shop_order.shipment_address && item.shop_order.shipment_address.last_name.includes(query))
        );
      }

      if (this.typeFilter) {
        filtered = filtered.filter(item => item.status === this.typeFilter);
      }

      return filtered;
    }
  },
  created() {
    this.fetchItems();
  },
  methods: {
    fetchItems() {
      this.loading = true;
      this.request({
        url: this.endpoint('file_handler/extractedPostReportItem/all'),
        params: {
          extracted_report: this.report.id,
        },
        method: "get",
        success: data => {
          this.items = data;
          this.loading = false
        }
      })
    },
    close() {
      this.$emit('close');
    },
    formatDate(dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('fa-IR', options);
    },
    formatDateTime(dateTimeString) {
      const date = new Date(dateTimeString);
      return date.toLocaleString('fa-IR');
    }
  }
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter, .modal-leave-to {
  opacity: 0;
}
</style>